import { useMemo, useState } from "react"
import { useQuery } from "urql"

import { COUNTRY_CODE_PRIORITY_INDEX_MAP } from "@/constants/locations"
import type { Country, Query, Region } from "@/graphql/codegen/graphql"
import { CountryListDocument, RegionListQueryDocument } from "@/graphql/codegen/graphql"

export interface UseGetCountriesAndRegionsReturn {
  regions: Region[] | any[]
  isRegionsFetching: boolean
  countries: Country[] | any[]
  isCountriesFetching: boolean
  setSelectedCountry: (value: ((prevState: string) => string) | string) => void
}

const COUNTRIES_LIMIT = 250
const REGIONS_LIMIT = 60

export const useGetCountriesAndRegions = (countryIsoCode?: string): UseGetCountriesAndRegionsReturn => {
  const [selectedCountry, setSelectedCountry] = useState(countryIsoCode ?? "")

  const [{ data: countriesData, fetching: isCountriesFetching }] = useQuery<Pick<Query, "country">>({
    query: CountryListDocument,
    variables: { input: { limit: COUNTRIES_LIMIT } },
  })

  const [{ data: regionsData, fetching: isRegionsFetching }] = useQuery<Pick<Query, "region">>({
    query: RegionListQueryDocument,
    variables: { input: { filter: { countryIsoCode: selectedCountry }, limit: REGIONS_LIMIT } },
  })

  const countries = useMemo(() => {
    if (countriesData?.country.list.__typename === "CountryListSuccess") {
      const countries = countriesData.country.list.countries

      return [...countries].sort((a, b) => {
        const aPriority = COUNTRY_CODE_PRIORITY_INDEX_MAP.has(a.isoCode)
          ? COUNTRY_CODE_PRIORITY_INDEX_MAP.get(a.isoCode)!
          : Infinity
        const bPriority = COUNTRY_CODE_PRIORITY_INDEX_MAP.has(b.isoCode)
          ? COUNTRY_CODE_PRIORITY_INDEX_MAP.get(b.isoCode)!
          : Infinity
        return aPriority - bPriority
      })
    }

    return []
  }, [countriesData])

  const regions = useMemo(() => {
    if (regionsData?.region.list.__typename === "RegionListSuccess") {
      return regionsData.region.list.regions
    }

    return []
  }, [regionsData])

  return {
    countries,
    isCountriesFetching,
    regions,
    isRegionsFetching,
    setSelectedCountry,
  }
}
